import { TypedSopNode } from "./_Base";
import { CoreGroup } from "../../../core/geometry/Group";
import { ObjectPropertiesSopOperation } from "../../operations/sop/ObjectProperties";
import { NodeParamsConfig, ParamConfig } from "../utils/params/ParamsConfig";
import { isBooleanTrue } from "../../../core/Type";
const DEFAULT = ObjectPropertiesSopOperation.DEFAULT_PARAMS;
class ObjectPropertiesSopParamsConfig extends NodeParamsConfig {
  constructor() {
    super(...arguments);
    this.applyToChildren = ParamConfig.BOOLEAN(DEFAULT.applyToChildren, { separatorAfter: true });
    this.tname = ParamConfig.BOOLEAN(DEFAULT.tname);
    this.name = ParamConfig.STRING(DEFAULT.name, {
      visibleIf: { tname: true },
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.trenderOrder = ParamConfig.BOOLEAN(DEFAULT.trenderOrder);
    this.renderOrder = ParamConfig.INTEGER(DEFAULT.renderOrder, {
      visibleIf: { trenderOrder: true },
      range: [0, 10],
      rangeLocked: [false, false],
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.tfrustumCulled = ParamConfig.BOOLEAN(DEFAULT.tfrustumCulled);
    this.frustumCulled = ParamConfig.BOOLEAN(DEFAULT.frustumCulled, {
      visibleIf: { tfrustumCulled: true },
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.tmatrixAutoUpdate = ParamConfig.BOOLEAN(DEFAULT.tmatrixAutoUpdate);
    this.matrixAutoUpdate = ParamConfig.BOOLEAN(DEFAULT.matrixAutoUpdate, {
      visibleIf: { tmatrixAutoUpdate: true },
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.tvisible = ParamConfig.BOOLEAN(DEFAULT.tvisible);
    this.visible = ParamConfig.BOOLEAN(DEFAULT.visible, {
      visibleIf: { tvisible: true },
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.tcastShadow = ParamConfig.BOOLEAN(DEFAULT.tcastShadow);
    this.castShadow = ParamConfig.BOOLEAN(DEFAULT.castShadow, {
      visibleIf: { tcastShadow: true },
      separatorAfter: true,
      expression: { forEntities: true }
    });
    this.treceiveShadow = ParamConfig.BOOLEAN(DEFAULT.treceiveShadow);
    this.receiveShadow = ParamConfig.BOOLEAN(DEFAULT.receiveShadow, {
      visibleIf: { treceiveShadow: true },
      expression: { forEntities: true }
    });
  }
}
const ParamsConfig = new ObjectPropertiesSopParamsConfig();
export class ObjectPropertiesSopNode extends TypedSopNode {
  constructor() {
    super(...arguments);
    this.paramsConfig = ParamsConfig;
  }
  static type() {
    return "objectProperties";
  }
  static displayedInputNames() {
    return ["objects to change properties of"];
  }
  initializeNode() {
    this.io.inputs.setCount(1);
    this.io.inputs.initInputsClonedState(ObjectPropertiesSopOperation.INPUT_CLONED_STATE);
  }
  async cook(inputCoreGroups) {
    this._operation = this._operation || new ObjectPropertiesSopOperation(this.scene(), this.states);
    const paramWithExpression = this.params.all.find((param) => param.hasExpression());
    if (paramWithExpression) {
      const coreGroup = inputCoreGroups[0];
      await this._cookWithExpressions(coreGroup);
      this.setCoreGroup(coreGroup);
    } else {
      const coreGroup = this._operation.cook(inputCoreGroups, this.pv);
      this.setCoreGroup(coreGroup);
    }
  }
  async _cookWithExpressions(coreGroup) {
    await this._cookWithExpressionsForCoreGroup(coreGroup);
    if (isBooleanTrue(this.pv.applyToChildren)) {
      const objects = coreGroup.objects();
      for (let object of objects) {
        const subCoreGroup = CoreGroup._fromObjects(object.children);
        await this._cookWithExpressionsForCoreGroup(subCoreGroup);
      }
    }
  }
  async _cookWithExpressionsForCoreGroup(coreGroup) {
    const entities = coreGroup.coreObjects();
    const pv = this.pv;
    if (isBooleanTrue(pv.tname)) {
      if (this.p.name.expressionController) {
        await this.p.name.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().name = value;
        });
      } else {
        entities.forEach((e) => e.object().name = pv.name);
      }
    }
    if (isBooleanTrue(pv.trenderOrder)) {
      if (this.p.renderOrder.expressionController) {
        await this.p.renderOrder.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().renderOrder = value;
        });
      } else {
        entities.forEach((e) => e.object().renderOrder = pv.renderOrder);
      }
    }
    if (isBooleanTrue(pv.tfrustumCulled)) {
      if (this.p.frustumCulled.expressionController) {
        await this.p.frustumCulled.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().frustumCulled = value;
        });
      } else {
        entities.forEach((e) => e.object().frustumCulled = pv.frustumCulled);
      }
    }
    if (isBooleanTrue(pv.tmatrixAutoUpdate)) {
      if (this.p.matrixAutoUpdate.expressionController) {
        await this.p.matrixAutoUpdate.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().matrixAutoUpdate = value;
        });
      } else {
        entities.forEach((e) => e.object().matrixAutoUpdate = pv.matrixAutoUpdate);
      }
    }
    if (isBooleanTrue(pv.tvisible)) {
      if (this.p.visible.expressionController) {
        await this.p.visible.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().visible = value;
        });
      } else {
        entities.forEach((e) => e.object().visible = pv.visible);
      }
    }
    if (isBooleanTrue(pv.tcastShadow)) {
      if (this.p.castShadow.expressionController) {
        await this.p.castShadow.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().castShadow = value;
        });
      } else {
        entities.forEach((e) => e.object().castShadow = pv.castShadow);
      }
    }
    if (isBooleanTrue(pv.treceiveShadow)) {
      if (this.p.receiveShadow.expressionController) {
        await this.p.receiveShadow.expressionController.computeExpressionForObjects(entities, (entity, value) => {
          entity.object().receiveShadow = value;
        });
      } else {
        entities.forEach((e) => e.object().receiveShadow = pv.receiveShadow);
      }
    }
  }
}
